import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { SOLUTIONS } from "@/data/content";
import { Button } from "@/components/ui/button";
import AnimatedVisual from "@/components/AnimatedVisual";

export default function SolutionsShowcase({ onOpenDemo }) {
  return (
    <section className="relative py-24 lg:py-32 bg-[#040611]" id="solutions" data-testid="home-solutions">
      <div className="absolute inset-0 grid-bg opacity-25" aria-hidden />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div className="max-w-2xl">
            <div className="overline">Solutions</div>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
              Launch faster with <span className="text-gradient-blue">white-label</span> & turnkey stacks.
            </h2>
          </div>
          <Button
            data-testid="solutions-cta"
            onClick={onOpenDemo}
            className="btn-glow bg-[#2563EB] hover:bg-[#1d4ed8] rounded-full h-11 px-6"
          >
            Get a Quote <ArrowRight className="ml-2 w-4 h-4"/>
          </Button>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
          {SOLUTIONS.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              data-testid={`solution-card-${i}`}
              className="group overflow-hidden rounded-3xl bg-[#0B1224]/80 border border-white/10"
            >
              <div className="relative aspect-[16/9] overflow-hidden">
                <AnimatedVisual variant={s.variant} className="absolute inset-0 group-hover:scale-105"
                                label={`${s.title} animation`}/>
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1224] via-[#0B1224]/40 to-transparent" />
                <div className="absolute bottom-4 left-5 right-5">
                  <div className="font-display text-2xl font-bold tracking-tight">{s.title}</div>
                  <div className="text-sm text-gray-300 mt-1">{s.subtitle}</div>
                </div>
              </div>
              <div className="p-6">
                <ul className="grid grid-cols-2 gap-y-3 gap-x-4">
                  {s.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-gray-300">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#00D4FF] flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 flex items-center justify-between">
                  <button
                    onClick={onOpenDemo}
                    data-testid={`solution-more-${i}`}
                    className="text-sm font-medium text-[#00D4FF] hover:text-white flex items-center gap-1.5"
                  >
                    Explore this stack <ArrowRight className="w-4 h-4" />
                  </button>
                  <div className="text-xs uppercase tracking-[0.2em] text-gray-500">Live in 72h</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
